import React from 'react'
import styled, { css } from 'styled-components'
import {
  type FlexboxProps,
  type LayoutProps,
  type SpaceProps,
} from 'styled-system'
import { ICON_SIZE, type IconProps } from 'components/Icon'
import { Box, Flex } from 'components/Layout'
import { H5 } from 'components/Typography'
import { COLOR_INTENT, FONT_SIZE, RADIUS, SPACE } from 'Theme'
import { useMedia } from 'useMedia'

export const CALLOUT_INTENT = {
  DEFAULT: COLOR_INTENT.CALLOUT.DEFAULT,
  DANGER: COLOR_INTENT.CALLOUT.DANGER,
  WARNING: COLOR_INTENT.CALLOUT.WARNING,
  INFO: COLOR_INTENT.CALLOUT.INFO,
  PRIMARY: COLOR_INTENT.CALLOUT.PRIMARY,
}

export const CALLOUT_VARIANT = {
  DEFAULT: 'default',
  MINIMAL: 'minimal',
  ROUNDED: 'rounded',
  ROUNDED_BOTTOM: 'roundedBottom',
}

const ChildrenWrapper = styled(Flex)`
  color: ${({ intent }) => intent.COLOR};
`

const Wrapper = styled(Flex)`
  background-color: ${({ intent }) => intent.BACKGROUND};
  ${({ variant }) =>
    variant === CALLOUT_VARIANT.ROUNDED &&
    css`
      border-radius: ${RADIUS.PX_6};
    `}
  ${({ variant }) =>
    variant === CALLOUT_VARIANT.ROUNDED_BOTTOM &&
    css`
      border-bottom-left-radius: ${RADIUS.PX_10};
      border-bottom-right-radius: ${RADIUS.PX_10};
    `}
`

interface CalloutProps extends FlexboxProps, SpaceProps, LayoutProps {
  intent?: (typeof CALLOUT_INTENT)[keyof typeof CALLOUT_INTENT]
  variant?: (typeof CALLOUT_VARIANT)[keyof typeof CALLOUT_VARIANT]
  title?: string
  icon?: React.ElementType<IconProps>
}

export const Callout: React.FC<React.PWC<CalloutProps>> = ({
  intent = CALLOUT_INTENT.DEFAULT,
  variant = CALLOUT_VARIANT.DEFAULT,
  title,
  icon: Icon,
  children,
  ...props
}) => {
  const media = useMedia()
  return (
    <Wrapper
      intent={intent}
      variant={variant}
      py={
        variant === CALLOUT_VARIANT.DEFAULT
          ? { MOBILE: SPACE.PX_10, TABLET: SPACE.PX_20 }
          : { MOBILE: SPACE.PX_5, TABLET: SPACE.PX_15 }
      }
      pl={
        variant === CALLOUT_VARIANT.DEFAULT
          ? { MOBILE: SPACE.PX_20, TABLET: Icon ? SPACE.PX_20 : SPACE.PX_30 }
          : { MOBILE: SPACE.PX_10, TABLET: Icon ? SPACE.PX_20 : SPACE.PX_15 }
      }
      pr={
        variant === CALLOUT_VARIANT.DEFAULT
          ? { MOBILE: SPACE.PX_20, TABLET: SPACE.PX_30 }
          : { MOBILE: SPACE.PX_10, TABLET: SPACE.PX_15 }
      }
      {...props}
    >
      {!media.MOBILE && Icon && (
        <Flex>
          <Icon color={intent.COLOR} size={ICON_SIZE.PX_24} />
        </Flex>
      )}

      <Flex
        ml={{ MOBILE: 0, TABLET: Icon ? SPACE.PX_15 : 0 }}
        flexDirection="column"
      >
        {title && (
          <Box>
            <H5
              {...(media.MOBILE ? { fontSize: FONT_SIZE.PX_14 } : {})}
              color={intent.COLOR}
            >
              {title}
            </H5>
          </Box>
        )}
        <ChildrenWrapper flexDirection="column" intent={intent}>
          {children}
        </ChildrenWrapper>
      </Flex>
    </Wrapper>
  )
}
